import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { getAuth } from "firebase/auth";
import {
  Box,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../../backend/firebaseConfig";
import ManageAppointmentPickersDay from "./ManageAppointmentPickersDay";
import {
  flattenAppointmentsSnapshot,
  getAppointmentCountsForDate,
  loadActiveDentistsFromUserSnapshot,
  buildAppointmentSlotMapForDates,
  buildMergedScheduleSlotMenuOptions,
  validateSchedulePlotAgainstAppointments,
  resolveAppointmentDentistUid,
  isBlockingAppointmentStatus,
} from "./clinicStaffCalendarHelpers";
import {
  countChairsWithPatientAppointments,
  dentistHasPlottedSchedule,
  getDentistPlottedSlotsForDates,
  buildScheduleWithDisplayChairsForDate,
  getLocalDateKeyFromDate,
  normalizeAvailableSlots,
  resolveChairCountForDate,
  saveChairScheduleForDates,
  saveDentistSchedulePlot,
  assignAppointmentsToChairsForBooking,
  isChairOccupiedAtRange,
  STANDARD_OFFICE_SLOT_LABELS,
  subscribeToChairScheduleForDate,
  subscribeToChairScheduledDates,
  subscribeToDentistSchedulesForDate,
  subscribeToDentistSchedulesForDateWithMeta,
  subscribeToPlottedDatesForDentist,
} from "../../utils/dentistSchedulePlots";
import {
  formatSlotRangeLabelFromMinutes,
  getAppointmentDisplayRangeMinutes,
  mergeMinuteRanges,
  parseClockLabelToMinutes,
} from "../../utils/appointmentTimeDisplay";

const SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: {
    sx: {
      maxHeight: 320,
      mt: 0.5,
    },
  },
};

const MODAL_SELECT_MENU_PROPS = {
  ...SELECT_MENU_PROPS,
  disablePortal: true,
  PaperProps: {
    ...SELECT_MENU_PROPS.PaperProps,
    sx: {
      ...SELECT_MENU_PROPS.PaperProps?.sx,
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(197, 165, 114, 0.25) !important",
        color: "#2C251E !important",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(197, 165, 114, 0.35) !important",
      },
      "& .MuiMenuItem-root.Mui-selected .MuiListItemText-primary": {
        color: "#2C251E !important",
      },
    },
  },
};

const CHAIR_SCHEDULE_NONE = 0;

const formatChairScheduleDateLabel = (dateKey) => {
  const parts = String(dateKey || "").split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return dateKey;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const dateHasPlottedDentistSchedules = (scheduleDocData) => {
  if (!scheduleDocData || typeof scheduleDocData !== "object") return false;
  return Object.keys(scheduleDocData).some((uid) =>
    dentistHasPlottedSchedule(scheduleDocData, uid)
  );
};

const ClinicStaffDashboardCalendar = ({ statsCard = null }) => {
  const auth = getAuth();
  const [selectedDateAppointment, setSelectedDateAppointment] = useState(dayjs());
  const [closedDays, setClosedDays] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [dentistReferenceScheduleForDate, setDentistReferenceScheduleForDate] = useState({});
  const [hideReferenceSummary, setHideReferenceSummary] = useState(false);
  const [chairScheduleForSelectedDate, setChairScheduleForSelectedDate] = useState(null);
  const [chairScheduleByDateKey, setChairScheduleByDateKey] = useState(() => new Map());

  const [showChairScheduleModal, setShowChairScheduleModal] = useState(false);
  const [chairScheduleModalCount, setChairScheduleModalCount] = useState(CHAIR_SCHEDULE_NONE);
  const [chairScheduleDates, setChairScheduleDates] = useState(() => new Set());
  const [chairScheduleSaving, setChairScheduleSaving] = useState(false);
  const [chairScheduleError, setChairScheduleError] = useState(null);
  const [chairSchedulePlotDataByDateKey, setChairSchedulePlotDataByDateKey] = useState({});

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDentistUid, setScheduleDentistUid] = useState("");
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleDates, setScheduleDates] = useState(() => new Set());
  const [scheduleDataForSelectedDate, setScheduleDataForSelectedDate] = useState({});
  /** YYYY-MM-DD for which scheduleDataForSelectedDate was last loaded (prevents stale cross-date chair layout). */
  const [schedulePlotLoadedDateKey, setSchedulePlotLoadedDateKey] = useState(null);
  const [chairScheduleLoadedDateKey, setChairScheduleLoadedDateKey] = useState(null);
  const [appointmentsSnapshotReady, setAppointmentsSnapshotReady] = useState(false);
  const [assignedPlotDates, setAssignedPlotDates] = useState(() => new Set());
  const [scheduleDataByDateKey, setScheduleDataByDateKey] = useState({});
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const [chairSnackbar, setChairSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const schedulePrefillKeyRef = useRef("");
  const userEditedScheduleRef = useRef(false);
  const latestPlotDataRef = useRef({});
  const plotServerFallbackTimerRef = useRef(null);
  const appointmentsServerFallbackTimerRef = useRef(null);
  const CHAIR_SCHEDULE_SERVER_FALLBACK_MS = 4000;

  const activeDentistCount = dentists.length;

  const selectedDateKey = useMemo(
    () => getLocalDateKeyFromDate(selectedDateAppointment.toDate()),
    [selectedDateAppointment]
  );

  const configuredChairCount = useMemo(() => {
    const mapCount = chairScheduleByDateKey.get(selectedDateKey);
    const dateChairSchedule =
      chairScheduleLoadedDateKey === selectedDateKey
        ? chairScheduleForSelectedDate
        : Number.isFinite(mapCount) && mapCount > 0
          ? { chairCount: mapCount }
          : null;
    return resolveChairCountForDate({
      dateChairSchedule,
      maxChairs: activeDentistCount,
    });
  }, [
    chairScheduleForSelectedDate,
    chairScheduleByDateKey,
    chairScheduleLoadedDateKey,
    selectedDateKey,
    activeDentistCount,
  ]);

  const chairOptions = useMemo(
    () => Array.from({ length: activeDentistCount }, (_, idx) => idx + 1),
    [activeDentistCount]
  );

  const chairScheduledDateKeys = useMemo(
    () => new Set(chairScheduleByDateKey.keys()),
    [chairScheduleByDateKey]
  );

  const getDentistDisplayName = useCallback((dentist) => {
    if (!dentist) return "";
    if (dentist.displayName) return dentist.displayName;
    const first = (dentist.firstName || "").trim();
    const last = (dentist.lastName || "").trim();
    const combined = [first, last].filter(Boolean).join(" ").trim();
    return combined ? `Dr. ${combined}` : "";
  }, []);

  const getAppointmentCountsForCalendarDate = useCallback(
    (date) => getAppointmentCountsForDate(date, allAppointments),
    [allAppointments]
  );

  const renderModalCalendarAppointmentBadges = useCallback(
    ({ date, view }) => {
      if (view !== "month") return null;

      const appointmentCounts = getAppointmentCountsForCalendarDate(date);
      const hasAppointments =
        appointmentCounts.pending > 0 ||
        appointmentCounts.confirmed > 0 ||
        appointmentCounts.completed > 0;

      if (!hasAppointments) return null;

      return (
        <div
          style={{
            position: "absolute",
            top: "4px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "2px",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {appointmentCounts.confirmed > 0 && (
            <div
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                backgroundColor: "#80cd97ff",
                border: "1px solid #59544A",
              }}
            />
          )}
          {appointmentCounts.pending > 0 && (
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#ffd48eff",
                border: "1px solid #59544A",
              }}
            />
          )}
        </div>
      );
    },
    [getAppointmentCountsForCalendarDate]
  );

  useEffect(() => {
    const closedDaysRef = doc(db, "closedDays", "closedDates");
    const unsubscribe = onSnapshot(
      closedDaysRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setClosedDays(data.dates || []);
        } else {
          setClosedDays([]);
        }
      },
      (error) => {
        if (error.code !== "permission-denied") {
          console.error("Error listening to closed days:", error);
        }
        setClosedDays([]);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const markAppointmentsReady = () => {
      if (appointmentsServerFallbackTimerRef.current) {
        clearTimeout(appointmentsServerFallbackTimerRef.current);
        appointmentsServerFallbackTimerRef.current = null;
      }
      setAppointmentsSnapshotReady(true);
    };

    appointmentsServerFallbackTimerRef.current = setTimeout(
      markAppointmentsReady,
      CHAIR_SCHEDULE_SERVER_FALLBACK_MS
    );

    const unsubscribe = onSnapshot(
      collection(db, "appointments"),
      { includeMetadataChanges: true },
      (snapshot) => {
        setAllAppointments(flattenAppointmentsSnapshot(snapshot));
        if (!snapshot.metadata.fromCache) {
          markAppointmentsReady();
        }
      },
      (error) => {
        if (error.code !== "permission-denied") {
          console.error("Error listening to appointments:", error);
        }
        setAllAppointments([]);
        markAppointmentsReady();
      }
    );
    return () => {
      if (appointmentsServerFallbackTimerRef.current) {
        clearTimeout(appointmentsServerFallbackTimerRef.current);
        appointmentsServerFallbackTimerRef.current = null;
      }
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      setDentists(loadActiveDentistsFromUserSnapshot(snapshot));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const dateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
    setDentistReferenceScheduleForDate({});
    const refDocRef = doc(db, "dentistReferenceSchedules", dateKey);
    const unsub = onSnapshot(refDocRef, (snap) => {
      setDentistReferenceScheduleForDate(snap.exists() ? snap.data() : {});
    }, () => setDentistReferenceScheduleForDate({}));
    return () => unsub();
  }, [selectedDateAppointment]);

  useEffect(() => {
    return subscribeToChairScheduledDates((next) => {
      setChairScheduleByDateKey(next);
    });
  }, []);

  useEffect(() => {
    const dateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
    setChairScheduleForSelectedDate(null);
    setChairScheduleLoadedDateKey(null);
    let cancelled = false;
    const unsub = subscribeToChairScheduleForDate(dateKey, (data) => {
      if (cancelled) return;
      setChairScheduleForSelectedDate(data);
      setChairScheduleLoadedDateKey(dateKey);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [selectedDateAppointment]);

  useEffect(() => {
    if (!showScheduleModal || !scheduleDentistUid) {
      setAssignedPlotDates(new Set());
      return undefined;
    }
    return subscribeToPlottedDatesForDentist(scheduleDentistUid, setAssignedPlotDates);
  }, [showScheduleModal, scheduleDentistUid]);

  const selectedScheduleDateKeys = useMemo(
    () => [...scheduleDates].sort(),
    [scheduleDates]
  );

  useEffect(() => {
    if (!showScheduleModal || selectedScheduleDateKeys.length === 0) {
      setScheduleDataByDateKey({});
      return undefined;
    }

    const unsubs = selectedScheduleDateKeys.map((dateKey) =>
      subscribeToDentistSchedulesForDate(dateKey, (data) => {
        setScheduleDataByDateKey((prev) => ({
          ...prev,
          [dateKey]: data || {},
        }));
      })
    );

    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [showScheduleModal, selectedScheduleDateKeys]);

  const selectedChairScheduleDateKeys = useMemo(
    () => [...chairScheduleDates].sort(),
    [chairScheduleDates]
  );

  useEffect(() => {
    if (!showChairScheduleModal || selectedChairScheduleDateKeys.length === 0) {
      setChairSchedulePlotDataByDateKey({});
      return undefined;
    }

    const unsubs = selectedChairScheduleDateKeys.map((dateKey) =>
      subscribeToDentistSchedulesForDate(dateKey, (data) => {
        setChairSchedulePlotDataByDateKey((prev) => ({
          ...prev,
          [dateKey]: data || {},
        }));
      })
    );

    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [showChairScheduleModal, selectedChairScheduleDateKeys]);

  // Real-time subscription to dentist schedules for the selected dashboard date (chair overview)
  useEffect(() => {
    const dateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
    setSchedulePlotLoadedDateKey(null);
    setScheduleDataForSelectedDate({});
    latestPlotDataRef.current = {};
    let cancelled = false;

    const applyPlotReady = (plotData) => {
      if (cancelled) return;
      const data = plotData || {};
      latestPlotDataRef.current = data;
      setScheduleDataForSelectedDate(data);
      setSchedulePlotLoadedDateKey(
        dateHasPlottedDentistSchedules(data) ? dateKey : null
      );
    };

    const clearPlotFallback = () => {
      if (plotServerFallbackTimerRef.current) {
        clearTimeout(plotServerFallbackTimerRef.current);
        plotServerFallbackTimerRef.current = null;
      }
    };

    plotServerFallbackTimerRef.current = setTimeout(() => {
      if (cancelled) return;
      applyPlotReady(latestPlotDataRef.current);
    }, CHAIR_SCHEDULE_SERVER_FALLBACK_MS);

    const unsub = subscribeToDentistSchedulesForDateWithMeta(
      dateKey,
      (data, meta) => {
        if (cancelled) return;
        latestPlotDataRef.current = data || {};
        if (meta?.isError) {
          setScheduleDataForSelectedDate({});
          setSchedulePlotLoadedDateKey(null);
          return;
        }
        if (!meta?.fromCache) {
          clearPlotFallback();
          applyPlotReady(data);
        }
      }
    );

    return () => {
      cancelled = true;
      clearPlotFallback();
      unsub();
    };
  }, [selectedDateAppointment]);

  // In the auto-assign model, chair conflicts are resolved on save.
  // The slot picker only shows appointment-based blocks (dentist's own bookings).
  const blockedSlotOwnerMap = useMemo(() => new Map(), []);
  const blockedScheduleSlots = useMemo(() => new Set(), []);

  useEffect(() => {
    if (
      !showScheduleModal ||
      !scheduleDentistUid ||
      selectedScheduleDateKeys.length === 0
    ) {
      return;
    }

    const allDatesLoaded = selectedScheduleDateKeys.every((dateKey) =>
      Object.prototype.hasOwnProperty.call(scheduleDataByDateKey, dateKey)
    );
    if (!allDatesLoaded) return;

    const { availableSlots: ownPlottedSlots } =
      getDentistPlottedSlotsForDates(scheduleDataByDateKey, {
        dateKeys: selectedScheduleDateKeys,
        dentistUid: scheduleDentistUid,
      });
    const ownPlottedSet = new Set(ownPlottedSlots);

    setScheduleSlots((prev) => {
      if (
        !userEditedScheduleRef.current &&
        ownPlottedSlots.length > 0
      ) {
        const unchanged =
          ownPlottedSlots.length === prev.length &&
          ownPlottedSlots.every((slot) => prev.includes(slot));
        if (!unchanged) {
          return ownPlottedSlots;
        }
      }

      const next = prev.filter((slot) => ownPlottedSet.has(slot) || !prev.includes(slot));
      return next.length === prev.length ? prev : next;
    });
  }, [
    showScheduleModal,
    scheduleDentistUid,
    selectedScheduleDateKeys,
    scheduleDataByDateKey,
  ]);

  useEffect(() => {
    if (!showScheduleModal || !scheduleDentistUid || selectedScheduleDateKeys.length === 0) {
      schedulePrefillKeyRef.current = "";
      userEditedScheduleRef.current = false;
      return;
    }

    const allDatesLoaded = selectedScheduleDateKeys.every((dateKey) =>
      Object.prototype.hasOwnProperty.call(scheduleDataByDateKey, dateKey)
    );
    if (!allDatesLoaded) return;

    const prefillKey = `${scheduleDentistUid}|${selectedScheduleDateKeys.join(",")}`;
    if (schedulePrefillKeyRef.current === prefillKey) return;

    schedulePrefillKeyRef.current = prefillKey;

    const { availableSlots, chairNumber } = getDentistPlottedSlotsForDates(
      scheduleDataByDateKey,
      {
        dateKeys: selectedScheduleDateKeys,
        dentistUid: scheduleDentistUid,
      }
    );

    if (availableSlots.length > 0) {
      userEditedScheduleRef.current = false;
      setScheduleSlots(availableSlots);
    } else if (!userEditedScheduleRef.current) {
      setScheduleSlots([]);
    }
  }, [
    showScheduleModal,
    scheduleDentistUid,
    selectedScheduleDateKeys,
    scheduleDataByDateKey,
  ]);

  const scheduleSlotMenuOptions = useMemo(() => {
    const appointmentSlotMap =
      scheduleDentistUid && selectedScheduleDateKeys.length > 0
        ? buildAppointmentSlotMapForDates(
            allAppointments,
            selectedScheduleDateKeys,
            scheduleDentistUid,
            STANDARD_OFFICE_SLOT_LABELS
          )
        : new Map();

    return buildMergedScheduleSlotMenuOptions(
      STANDARD_OFFICE_SLOT_LABELS,
      appointmentSlotMap,
      blockedSlotOwnerMap
    );
  }, [
    allAppointments,
    scheduleDentistUid,
    selectedScheduleDateKeys,
    blockedSlotOwnerMap,
  ]);

  const dentistsWithScheduleOnSelectedDates = useMemo(() => {
    const uids = new Set();
    if (selectedScheduleDateKeys.length === 0) return uids;

    selectedScheduleDateKeys.forEach((dateKey) => {
      if (!Object.prototype.hasOwnProperty.call(scheduleDataByDateKey, dateKey)) return;
      const scheduleData = scheduleDataByDateKey[dateKey] || {};
      dentists.forEach((dentist) => {
        if (dentistHasPlottedSchedule(scheduleData, dentist.uid)) {
          uids.add(dentist.uid);
        }
      });
    });
    return uids;
  }, [selectedScheduleDateKeys, scheduleDataByDateKey, dentists]);

  const toggleScheduleSlotOption = useCallback(
    (option) => {
      if (!option || !Array.isArray(option.segmentSlots) || option.segmentSlots.length === 0) {
        return;
      }
      if (option.segmentSlots.some((slot) => blockedScheduleSlots.has(slot))) {
        return;
      }

      const allSelected = option.segmentSlots.every((slot) => scheduleSlots.includes(slot));
      userEditedScheduleRef.current = true;
      setScheduleSlots((prev) => {
        if (allSelected) {
          return prev.filter((slot) => !option.segmentSlots.includes(slot));
        }
        const next = new Set([...prev, ...option.segmentSlots]);
        return STANDARD_OFFICE_SLOT_LABELS.filter((slot) => next.has(slot));
      });
    },
    [blockedScheduleSlots, scheduleSlots]
  );

  const handleSaveChairSchedule = async () => {
    if (chairScheduleDates.size === 0) {
      setChairScheduleError("Please select at least one date on the calendar.");
      return;
    }
    if (
      chairScheduleModalCount !== CHAIR_SCHEDULE_NONE &&
      (!Number.isFinite(chairScheduleModalCount) || chairScheduleModalCount <= 0)
    ) {
      setChairScheduleError("Please select the number of chairs.");
      return;
    }
    const datesToSave = [...chairScheduleDates];
    if (
      getChairScheduleClearBlockMessage(datesToSave) ||
      isChairScheduleReduceBlocked(datesToSave)
    ) {
      return;
    }

    setChairScheduleSaving(true);
    setChairScheduleError(null);
    try {
      await saveChairScheduleForDates({
        dateKeys: datesToSave,
        chairCount: chairScheduleModalCount,
        updatedByUid: auth.currentUser ? auth.currentUser.uid : null,
      });

      setChairScheduleByDateKey((prev) => {
        const next = new Map(prev);
        datesToSave.forEach((dateKey) => {
          if (chairScheduleModalCount === CHAIR_SCHEDULE_NONE) {
            next.delete(dateKey);
          } else {
            next.set(dateKey, chairScheduleModalCount);
          }
        });
        return next;
      });

      const selectedDateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
      if (datesToSave.includes(selectedDateKey)) {
        setChairScheduleForSelectedDate(
          chairScheduleModalCount === CHAIR_SCHEDULE_NONE
            ? null
            : { chairCount: chairScheduleModalCount }
        );
      }

      setShowChairScheduleModal(false);
      setChairSnackbar({
        open: true,
        message:
          chairScheduleModalCount === CHAIR_SCHEDULE_NONE
            ? `Chair schedule cleared for ${datesToSave.length} date${
                datesToSave.length === 1 ? "" : "s"
              }.`
            : `Chair schedule saved for ${datesToSave.length} date${
                datesToSave.length === 1 ? "" : "s"
              } (${chairScheduleModalCount} chair${
                chairScheduleModalCount === 1 ? "" : "s"
              }).`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving chair schedule:", error);
      setChairScheduleError(error.message || "Could not save chair schedule.");
    } finally {
      setChairScheduleSaving(false);
    }
  };

  const openChairScheduleModal = () => {
    const existingCount = Number(chairScheduleForSelectedDate?.chairCount);
    const selectedDateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
    setChairScheduleModalCount(
      Number.isFinite(existingCount) && existingCount > 0
        ? existingCount
        : 2
    );
    // Pre-select the dashboard date so saving applies to the day staff are viewing.
    setChairScheduleDates(new Set([selectedDateKey]));
    setChairScheduleError(null);
    setShowChairScheduleModal(true);
  };

  const toggleChairScheduleDate = (date) => {
    const key = getLocalDateKeyFromDate(date);
    setChairScheduleError(null);
    setChairScheduleDates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const openScheduleModal = () => {
    const selectedDateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
    schedulePrefillKeyRef.current = "";
    userEditedScheduleRef.current = false;
    setScheduleDentistUid(dentists[0]?.uid || "");
    setScheduleSlots([]);
    setScheduleDataByDateKey({});
    setScheduleError(null);
    // Pre-select the dashboard date when it already has a chair schedule configured.
    setScheduleDates(
      new Set(chairScheduleByDateKey.has(selectedDateKey) ? [selectedDateKey] : [])
    );
    setShowScheduleModal(true);
  };

  const toggleSchedulePlotDate = (date) => {
    const key = getLocalDateKeyFromDate(date);
    if (!chairScheduleByDateKey.has(key)) return;
    schedulePrefillKeyRef.current = "";
    userEditedScheduleRef.current = false;
    setScheduleDates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSaveSchedule = async () => {
    if (!scheduleDentistUid) {
      setScheduleError("Please select a dentist.");
      return;
    }
    if (scheduleDates.size === 0) {
      const hasPendingChanges =
        userEditedScheduleRef.current || scheduleSlots.length > 0;
      if (!hasPendingChanges) {
        setScheduleError(null);
        setShowScheduleModal(false);
        return;
      }
      setScheduleError("Please select at least one date on the calendar.");
      return;
    }

    const slotsToSave = [...scheduleSlots];
    const datesToSave = [...scheduleDates];

    const datesWithoutChairs = datesToSave.filter(
      (dateKey) => !chairScheduleByDateKey.has(dateKey)
    );
    if (datesWithoutChairs.length > 0) {
      setScheduleError("Selected dates must have a chair schedule configured first.");
      return;
    }

    const hasExistingPlotOnSelectedDates = datesToSave.some((dateKey) =>
      dentistHasPlottedSchedule(scheduleDataByDateKey[dateKey], scheduleDentistUid)
    );

    if (slotsToSave.length === 0) {
      if (!hasExistingPlotOnSelectedDates) {
        setScheduleError("Please select at least one time slot.");
        return;
      }
    }

    setScheduleSaving(true);
    setScheduleError(null);
    try {
      const dentist = dentists.find((entry) => entry.uid === scheduleDentistUid);
      const dentistDisplayName =
        dentist?.displayName || getDentistDisplayName(dentist) || "This dentist";

      validateSchedulePlotAgainstAppointments({
        dentistDisplayName,
        dentistUid: scheduleDentistUid,
        dateKeys: datesToSave,
        selectedSlots: slotsToSave,
        allAppointments,
      });

      // Build per-date appointments map for auto-assign conflict resolution
      const appointmentsByDateKey = {};
      datesToSave.forEach((dateKey) => {
        appointmentsByDateKey[dateKey] = allAppointments.filter(
          (appt) => appt.date === dateKey
        );
      });

      const chairCountByDateKey = {};
      datesToSave.forEach((dateKey) => {
        chairCountByDateKey[dateKey] = resolveChairCountForDate({
          dateChairSchedule: chairScheduleByDateKey.has(dateKey)
            ? { chairCount: chairScheduleByDateKey.get(dateKey) }
            : null,
          maxChairs: activeDentistCount,
        });
      });

      await saveDentistSchedulePlot({
        dentistUid: scheduleDentistUid,
        dateKeys: datesToSave,
        availableSlots: slotsToSave,
        chairCountByDateKey,
        appointmentsByDateKey,
        updatedByUid: auth.currentUser ? auth.currentUser.uid : null,
      });

      userEditedScheduleRef.current = false;
      setShowScheduleModal(false);
      setChairSnackbar({
        open: true,
        message: slotsToSave.length
          ? "Dentist schedule saved."
          : "Dentist schedule removed successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving dentist schedule:", error);
      setScheduleError(error.message || "Could not save dentist schedule.");
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    setSelectedDateAppointment(newDate);
  };

  const scheduleDateCountLabel =
    scheduleDates.size === 0
      ? "No dates selected"
      : `${scheduleDates.size} date${scheduleDates.size === 1 ? "" : "s"} selected`;

  const chairScheduleDateCountLabel =
    chairScheduleDates.size === 0
      ? "No dates selected"
      : `${chairScheduleDates.size} date${chairScheduleDates.size === 1 ? "" : "s"} selected`;

  const chairCountsAssignedOnSelectedDates = useMemo(() => {
    const counts = new Set();
    let highlightNone = false;
    chairScheduleDates.forEach((dateKey) => {
      const count = chairScheduleByDateKey.get(dateKey);
      if (Number.isFinite(count) && count > 0) {
        counts.add(count);
      } else {
        highlightNone = true;
      }
    });
    return { counts, highlightNone };
  }, [chairScheduleDates, chairScheduleByDateKey]);

  const assignedChairMenuItemSx = {
    backgroundColor: "rgba(197, 165, 114, 0.12) !important",
    border: "2px solid rgba(197, 165, 114, 0.55) !important",
    borderRadius: "8px",
    margin: "2px 8px",
    fontWeight: 600,
    color: "#2C251E !important",
    "&.Mui-selected": {
      backgroundColor: "rgba(197, 165, 114, 0.25) !important",
      color: "#2C251E !important",
    },
  };

  const getChairScheduleClearBlockMessage = useCallback(
    (dateKeys) => {
      if (chairScheduleModalCount !== CHAIR_SCHEDULE_NONE) return null;

      const datesToClear = dateKeys.filter((dateKey) => chairScheduleByDateKey.has(dateKey));
      if (datesToClear.length === 0) return null;

      const plotsLoaded = datesToClear.every((dateKey) =>
        Object.prototype.hasOwnProperty.call(chairSchedulePlotDataByDateKey, dateKey)
      );
      if (!plotsLoaded) return null;

      const blockedDates = datesToClear
        .map((dateKey) => {
          const plotData = chairSchedulePlotDataByDateKey[dateKey] || {};
          const hasDentistSchedules = dateHasPlottedDentistSchedules(plotData);
          const hasAppointments = allAppointments.some(
            (appt) =>
              appt?.date === dateKey && isBlockingAppointmentStatus(appt.status)
          );
          if (!hasDentistSchedules && !hasAppointments) return null;
          return { dateKey, hasDentistSchedules, hasAppointments };
        })
        .filter(Boolean);

      if (blockedDates.length === 0) return null;

      if (blockedDates.length === 1) {
        const { dateKey, hasDentistSchedules, hasAppointments } = blockedDates[0];
        const label = formatChairScheduleDateLabel(dateKey);
        if (hasDentistSchedules && hasAppointments) {
          return `Cannot clear the chair schedule for ${label}. Remove dentist schedules and cancel or reassign appointments first.`;
        }
        if (hasDentistSchedules) {
          return `Cannot clear the chair schedule for ${label}. Remove dentist schedules for this date first.`;
        }
        return `Cannot clear the chair schedule for ${label}. Cancel or reassign appointments on this date first.`;
      }

      const hasDentistSchedules = blockedDates.some((entry) => entry.hasDentistSchedules);
      const hasAppointments = blockedDates.some((entry) => entry.hasAppointments);
      if (hasDentistSchedules && hasAppointments) {
        return "Cannot clear the chair schedule for the selected dates. Remove dentist schedules and cancel or reassign appointments first.";
      }
      if (hasDentistSchedules) {
        return "Cannot clear the chair schedule for the selected dates. Remove dentist schedules first.";
      }
      return "Cannot clear the chair schedule for the selected dates. Cancel or reassign appointments first.";
    },
    [
      chairScheduleModalCount,
      chairScheduleByDateKey,
      chairSchedulePlotDataByDateKey,
      allAppointments,
    ]
  );

  const chairScheduleClearBlockMessage = useMemo(
    () => getChairScheduleClearBlockMessage(selectedChairScheduleDateKeys),
    [getChairScheduleClearBlockMessage, selectedChairScheduleDateKeys]
  );

  const isChairScheduleReduceBlocked = useCallback(
    (dateKeys) => {
      const proposed = Number(chairScheduleModalCount);
      if (!Number.isFinite(proposed) || proposed <= 0) return false;

      const dashboardDateKey = getLocalDateKeyFromDate(selectedDateAppointment.toDate());
      return (Array.isArray(dateKeys) ? dateKeys : []).some((dateKey) => {
        const currentCount = Number(chairScheduleByDateKey.get(dateKey));
        if (!Number.isFinite(currentCount) || currentCount <= 0) return false;
        if (proposed >= currentCount) return false;

        const plotData =
          chairSchedulePlotDataByDateKey[dateKey] ||
          (dateKey === dashboardDateKey ? scheduleDataForSelectedDate : null) ||
          {};

        const chairsInUse = countChairsWithPatientAppointments({
          appointments: allAppointments,
          dateKey,
          chairCount: currentCount,
          scheduleDocData: plotData,
        });

        return chairsInUse > proposed;
      });
    },
    [
      chairScheduleModalCount,
      chairScheduleByDateKey,
      chairSchedulePlotDataByDateKey,
      scheduleDataForSelectedDate,
      selectedDateAppointment,
      allAppointments,
    ]
  );

  const isChairScheduleReduceBlockedForSelection = useMemo(
    () => isChairScheduleReduceBlocked(selectedChairScheduleDateKeys),
    [isChairScheduleReduceBlocked, selectedChairScheduleDateKeys]
  );

  const isChairScheduleSaveDisabled =
    chairScheduleSaving ||
    chairScheduleDates.size === 0 ||
    Boolean(chairScheduleClearBlockMessage) ||
    isChairScheduleReduceBlockedForSelection;

  const dateAppointments = useMemo(
    () => allAppointments.filter((appt) => appt.date === selectedDateKey),
    [allAppointments, selectedDateKey]
  );

  const hasPlottedSchedulesForSelectedDate = useMemo(
    () => dateHasPlottedDentistSchedules(scheduleDataForSelectedDate),
    [scheduleDataForSelectedDate]
  );

  const isChairSchedulePlotReady =
    schedulePlotLoadedDateKey === selectedDateKey && hasPlottedSchedulesForSelectedDate;

  const isChairScheduleMetaReady =
    chairScheduleLoadedDateKey === selectedDateKey ||
    chairScheduleByDateKey.has(selectedDateKey);

  const isChairScheduleReady =
    appointmentsSnapshotReady &&
    isChairSchedulePlotReady &&
    isChairScheduleMetaReady &&
    configuredChairCount > 0;

  const scheduleDataWithDisplayChairs = useMemo(() => {
    if (!isChairScheduleReady) return {};
    return buildScheduleWithDisplayChairsForDate(
      scheduleDataForSelectedDate,
      dateAppointments,
      configuredChairCount,
      { useAppointmentChairAnchors: false }
    );
  }, [
    scheduleDataForSelectedDate,
    dateAppointments,
    configuredChairCount,
    isChairScheduleReady,
  ]);

  // Chair timetable uses the same placement rules as patient booking (plotted chair first).
  const chairTableData = useMemo(() => {
    if (!isChairScheduleReady) return [];

    return assignAppointmentsToChairsForBooking({
      appointments: dateAppointments,
      chairCount: configuredChairCount,
      scheduleDocData: scheduleDataWithDisplayChairs,
      sortByClockTimeOnly: true,
    });
  }, [
    configuredChairCount,
    dateAppointments,
    scheduleDataWithDisplayChairs,
    isChairScheduleReady,
  ]);

  const chairTableDateLabel = useMemo(() => {
    const d = selectedDateAppointment.toDate();
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }, [selectedDateAppointment]);

  const dentistScheduleSummaryForDate = useMemo(() => {
    const formatDefaultTime = (timeStr) => {
      if (!timeStr) return "";
      const [h, m] = timeStr.split(":").map(Number);
      const suffix = h < 12 ? "AM" : "PM";
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
    };

    const plotted = [];
    Object.entries(scheduleDataForSelectedDate).forEach(([uid, entry]) => {
      if (!entry || typeof entry !== "object") return;
      if (!dentistHasPlottedSchedule(scheduleDataForSelectedDate, uid)) return;
      const dentist = dentists.find((d) => d.uid === uid);
      if (!dentist) return;
      const slots = normalizeAvailableSlots(entry.availableSlots || []);
      const ranges = slots
        .map((slot) => {
          const parts = slot.split(" - ");
          const start = parseClockLabelToMinutes(parts[0]);
          const end = parseClockLabelToMinutes(parts[1]);
          return { start, end };
        })
        .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end));
      const merged = mergeMinuteRanges(ranges);
      const timeLabel = merged
        .map(({ start, end }) => formatSlotRangeLabelFromMinutes(start, end))
        .join(", ");
      const cn = Number(entry.chairNumber);
      plotted.push({
        uid,
        name: dentist.displayName || getDentistDisplayName(dentist),
        timeLabel,
        chairNumber: Number.isFinite(cn) && cn > 0 ? cn : null,
        isReference: false,
      });
    });

    plotted.sort((a, b) => {
      if (a.chairNumber && b.chairNumber) return a.chairNumber - b.chairNumber;
      if (a.chairNumber) return -1;
      if (b.chairNumber) return 1;
      return a.name.localeCompare(b.name);
    });

    const plottedUids = new Set(plotted.map((e) => e.uid));

    // Date-specific reference schedules set by the dentist themselves
    const dateReference = [];
    Object.entries(dentistReferenceScheduleForDate).forEach(([uid, entry]) => {
      if (plottedUids.has(uid)) return;
      if (!entry || typeof entry !== "object") return;
      const dentist = dentists.find((d) => d.uid === uid);
      if (!dentist) return;
      const fromLabel = entry.from ? formatDefaultTime(entry.from) : null;
      const toLabel = entry.to ? formatDefaultTime(entry.to) : null;
      dateReference.push({
        uid,
        name: dentist.displayName || getDentistDisplayName(dentist),
        timeLabel: fromLabel && toLabel ? `${fromLabel} – ${toLabel}` : fromLabel || toLabel || "",
        chairNumber: null,
        isReference: true,
        referenceKind: "date",
      });
    });
    dateReference.sort((a, b) => a.name.localeCompare(b.name));
    const dateReferenceUids = new Set(dateReference.map((e) => e.uid));

    // Default (account-level) reference — dentists with no plotted or date-specific reference
    const reference = dentists
      .filter(
        (d) =>
          !plottedUids.has(d.uid) &&
          !dateReferenceUids.has(d.uid) &&
          (d.defaultAvailableFrom || d.defaultAvailableTo)
      )
      .map((d) => ({
        uid: d.uid,
        name: d.displayName || getDentistDisplayName(d),
        timeLabel:
          d.defaultAvailableFrom && d.defaultAvailableTo
            ? `${formatDefaultTime(d.defaultAvailableFrom)} – ${formatDefaultTime(d.defaultAvailableTo)}`
            : d.defaultAvailableFrom
              ? `From ${formatDefaultTime(d.defaultAvailableFrom)}`
              : `Until ${formatDefaultTime(d.defaultAvailableTo)}`,
        chairNumber: null,
        isReference: true,
        referenceKind: "default",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...plotted, ...dateReference, ...reference];
  }, [scheduleDataForSelectedDate, dentistReferenceScheduleForDate, dentists, getDentistDisplayName]);

  // Per-dentist available minute ranges + Firebase chair for the selected date.
  const allDentistSlotsForDate = useMemo(() => {
    const result = {};
    Object.entries(scheduleDataWithDisplayChairs).forEach(([uid, entry]) => {
      if (!entry || typeof entry !== "object") return;
      const ranges = normalizeAvailableSlots(entry.availableSlots || []).map(slot => {
        const parts = slot.split(" - ");
        const s = parseClockLabelToMinutes(parts[0]);
        const e = parseClockLabelToMinutes(parts[1]);
        return [s, e];
      }).filter(([s, e]) => !isNaN(s) && !isNaN(e));
      if (ranges.length > 0) {
        const cn = Number(entry.chairNumber);
        result[uid] = {
          ranges,
          chairNumber: Number.isFinite(cn) && cn > 0 ? cn : null,
        };
      }
    });
    return result;
  }, [scheduleDataWithDisplayChairs]);

  const timetableRows = useMemo(() => {
    if (!isChairScheduleReady || chairTableData.length === 0) {
      return [];
    }

    // Appointment-first timeline: boundaries from bookings + plots; 30-min grid only in open gaps
    const appointmentRanges = [];
    chairTableData.forEach(({ appointments }) => {
      (Array.isArray(appointments) ? appointments : []).forEach((appt) => {
        const range = getAppointmentDisplayRangeMinutes(appt);
        if (range) appointmentRanges.push(range);
      });
    });

    const breakpointSet = new Set();
    appointmentRanges.forEach(({ start, end }) => {
      breakpointSet.add(start);
      breakpointSet.add(end);
    });
    Object.values(allDentistSlotsForDate).forEach(({ ranges }) => {
      ranges.forEach(([s, e]) => {
        breakpointSet.add(s);
        breakpointSet.add(e);
      });
    });

    const isInsideAppointmentInterior = (minute) =>
      appointmentRanges.some((r) => r.start < minute && minute < r.end);

    let officeStart = Infinity;
    let officeEnd = -Infinity;
    STANDARD_OFFICE_SLOT_LABELS.forEach((slot) => {
      const parts = slot.split(" - ");
      const s = parseClockLabelToMinutes(parts[0]);
      const e = parseClockLabelToMinutes(parts[1]);
      if (!isNaN(s)) officeStart = Math.min(officeStart, s);
      if (!isNaN(e)) officeEnd = Math.max(officeEnd, e);
    });
    if (!Number.isFinite(officeStart) || !Number.isFinite(officeEnd)) return [];
    breakpointSet.add(officeStart);
    breakpointSet.add(officeEnd);

    STANDARD_OFFICE_SLOT_LABELS.forEach((slot) => {
      const parts = slot.split(" - ");
      const s = parseClockLabelToMinutes(parts[0]);
      const e = parseClockLabelToMinutes(parts[1]);
      [s, e].forEach((m) => {
        if (Number.isFinite(m) && !isInsideAppointmentInterior(m)) {
          breakpointSet.add(m);
        }
      });
    });

    const sortedBreakpoints = [...breakpointSet].sort((a, b) => a - b);
    if (sortedBreakpoints.length < 2) return [];

    const getApptRangeMinutes = (appt) => getAppointmentDisplayRangeMinutes(appt);

    const appointmentOverlapsRow = (appt, rowStart, rowEnd) => {
      const range = getApptRangeMinutes(appt);
      if (!range) return false;
      return range.start < rowEnd && range.end > rowStart;
    };

    // Returns the chair a dentist should visually "own" at a given time row.
    // Uses the most recent appointment before the row (or first upcoming after) so
    // "Available" always appears on the same column as the dentist's actual work,
    // regardless of which chair their home/rebalanced assignment points to.
    const resolveEffectiveChairForDentist = (uid, rowStart, rowEnd, homeChair) => {
      let lastBefore = null; // { endMin, chairNum }
      let firstAfter = null; // { startMin, chairNum }
      for (const { chairNum: cn, appointments } of chairTableData) {
        for (const appt of appointments) {
          if (resolveAppointmentDentistUid(appt) !== uid) continue;
          const range = getApptRangeMinutes(appt);
          if (!range) continue;
          const { start: aStart, end: aEnd } = range;
          if (aEnd <= rowStart) {
            if (!lastBefore || aEnd > lastBefore.endMin) lastBefore = { endMin: aEnd, chairNum: cn };
          } else if (aStart >= rowEnd) {
            if (!firstAfter || aStart < firstAfter.startMin) firstAfter = { startMin: aStart, chairNum: cn };
          }
        }
      }
      if (lastBefore) return lastBefore.chairNum;
      if (firstAfter) return firstAfter.chairNum;
      return homeChair;
    };

    // Plotted on this chair, or another dentist is scheduled this window but their
    // preferred chair is blocked by someone else's patient (e.g. Associate 9:00–9:30
    // books on chair 2 while chair 1 has Dr. Daniel's visit).
    const isChairCoveredForRow = (chairNum, rowStart, rowEnd) => {
      // A dentist covers this chair at this time only if their time-aware effective
      // chair (last/next appointment's chair) resolves to this chair, they have schedule
      // coverage, and they are not currently occupied on a different chair.
      const hasFreeDirectDentist = Object.entries(allDentistSlotsForDate).some(
        ([uid, { ranges, chairNumber }]) => {
          const effectiveChair = resolveEffectiveChairForDentist(uid, rowStart, rowEnd, chairNumber);
          if (effectiveChair !== chairNum) return false;
          if (!ranges.some(([s, e]) => s <= rowStart && e >= rowEnd)) return false;
          // Dentist is on this chair — confirm they're not busy on another chair right now
          return !chairTableData.some(({ chairNum: cn, appointments }) => {
            if (cn === chairNum) return false;
            return appointments.some(
              (appt) =>
                resolveAppointmentDentistUid(appt) === uid &&
                appointmentOverlapsRow(appt, rowStart, rowEnd)
            );
          });
        }
      );
      if (hasFreeDirectDentist) return true;

      if (isChairOccupiedAtRange(chairTableData, chairNum, rowStart, rowEnd)) {
        return false;
      }

      const dentistsForRow = Object.entries(allDentistSlotsForDate).filter(([, { ranges }]) =>
        ranges.some(([s, e]) => s <= rowStart && e >= rowEnd)
      );
      if (dentistsForRow.length === 0) return false;

      return dentistsForRow.some(([uid, { chairNumber: plottedChair }]) => {
        const preferred = resolveEffectiveChairForDentist(uid, rowStart, rowEnd, plottedChair) || 1;
        if (preferred === chairNum) return false;
        if (!isChairOccupiedAtRange(chairTableData, preferred, rowStart, rowEnd)) {
          return false;
        }
        const preferredBucket = chairTableData.find((c) => c.chairNum === preferred);
        const blockingAppt = (preferredBucket?.appointments || []).find((appt) =>
          appointmentOverlapsRow(appt, rowStart, rowEnd)
        );
        const blockingUid = blockingAppt
          ? resolveAppointmentDentistUid(blockingAppt)
          : null;
        return blockingUid !== uid;
      });
    };

    const pickOverlappingAppointment = (appointments, rowStart, rowEnd, continueAppt) => {
      const list = Array.isArray(appointments) ? appointments : [];
      const overlapping = list.filter((appt) =>
        appointmentOverlapsRow(appt, rowStart, rowEnd)
      );
      if (overlapping.length === 0) return null;
      if (continueAppt && overlapping.includes(continueAppt)) return continueAppt;
      const startingHere = overlapping.filter((appt) => {
        const range = getApptRangeMinutes(appt);
        return range && range.start <= rowStart;
      });
      if (startingHere.length === 1) return startingHere[0];
      return overlapping[0];
    };

    const lastApptByChairIdx = new Array(configuredChairCount).fill(null);
    const rows = [];
    for (let i = 0; i < sortedBreakpoints.length - 1; i++) {
      const rowStartMin = sortedBreakpoints[i];
      const rowEndMin = sortedBreakpoints[i + 1];
      const slotLabel = formatSlotRangeLabelFromMinutes(rowStartMin, rowEndMin);

      const apptPerChair = chairTableData.map(({ appointments }, chairIdx) => {
        const picked = pickOverlappingAppointment(
          appointments,
          rowStartMin,
          rowEndMin,
          lastApptByChairIdx[chairIdx]
        );
        lastApptByChairIdx[chairIdx] = picked;
        return picked;
      });

      const chairCells = chairTableData.map(({ chairNum }, idx) => {
        const apptForSlot = apptPerChair[idx];
        const isAvailable =
          !apptForSlot && isChairCoveredForRow(chairNum, rowStartMin, rowEndMin);
        const apptDentistUid = apptForSlot ? resolveAppointmentDentistUid(apptForSlot) : null;
        const apptDentistObj = apptDentistUid ? dentists.find(d => d.uid === apptDentistUid) : null;
        const apptDentist = apptDentistObj ? { uid: apptDentistUid, displayName: apptDentistObj.displayName } : null;
        return { chairNum, isAvailable, appointment: apptForSlot, apptDentist, rowSpan: 1, hidden: false };
      });
      // All chairs are occupied by existing appointments — no free slot for a new patient.
      const isChairFull = chairCells.filter(c => !c.hidden).every(c => !!c.appointment);
      const rowDurationMin = rowEndMin - rowStartMin;
      rows.push({
        slotLabel,
        rowStartMin,
        rowEndMin,
        rowDurationMin,
        chairCells,
        isChairFull,
      });
    }

    // Post-process: only merge cells that belong to the same multi-slot appointment
    const isSameGroup = (a, b) => {
      if (!a || !b) return false;
      if (a.appointment && b.appointment) return a.appointment === b.appointment;
      return false;
    };
    for (let col = 0; col < configuredChairCount; col++) {
      let i = 0;
      while (i < rows.length) {
        const cell = rows[i].chairCells[col];
        let span = 1;
        while (i + span < rows.length && isSameGroup(cell, rows[i + span].chairCells[col])) {
          span += 1;
        }
        rows[i].chairCells[col].rowSpan = span;
        for (let j = 1; j < span; j++) rows[i + j].chairCells[col].hidden = true;
        i += span;
      }
    }

    return rows;
  }, [
    chairTableData,
    allDentistSlotsForDate,
    configuredChairCount,
    dentists,
    isChairScheduleReady,
  ]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 2, width: "100%", alignItems: "flex-start" }}>
        {/* Left column: Chair Config + Calendar + Schedule Summary */}
        <Box sx={{ width: { xs: "100%", lg: "370px" }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "calc(100vh - 140px)", overflow: "hidden" }}>
          {/* Blue box: stats + config buttons + calendar */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, backgroundColor: "#fefefe", borderRadius: "16px", border: "1px solid #0000010ff", boxShadow: "0 1px 5px rgba(0,0,0,0.4)", padding: "12px" }}>
          {statsCard}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "10px",
              border: "1px solid rgba(197,165,114,0.35)",
              padding: "8px 12px",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600, fontSize: "12px", mb: 0.75 }}>
              Dentist Schedule Configuration
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#C5A572",
                  color: "#C5A572",
                  textTransform: "none",
                  borderRadius: "20px",
                  height: 30,
                  px: 1.5,
                  whiteSpace: "nowrap",
                  flex: 1,
                  fontSize: "12px",
                }}
                onClick={openChairScheduleModal}
                disabled={activeDentistCount <= 0}
              >
                Set Chair Schedule
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#C5A572",
                  color: "#C5A572",
                  textTransform: "none",
                  borderRadius: "20px",
                  height: 30,
                  px: 1.5,
                  whiteSpace: "nowrap",
                  flex: 1,
                  fontSize: "12px",
                }}
                onClick={openScheduleModal}
                disabled={activeDentistCount === 0 || chairScheduleByDateKey.size === 0}
              >
                Set Dentist Schedule
              </Button>
            </Stack>
          </Box>

          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDatePicker
                value={selectedDateAppointment}
                onChange={handleDateChange}
                showDaysOutsideCurrentMonth
                slots={{
                  day: ManageAppointmentPickersDay,
                }}
                slotProps={{
                  layout: {
                    sx: {
                      borderRadius: 2,
                      boxShadow: "0px 0.5px 10px .6px rgba(46, 45, 45, .6)",
                      p: 0.5,
                      width: "100%",
                      maxWidth: 320,
                    },
                  },
                  toolbar: {
                    sx: {
                      display: "none",
                    },
                  },
                  actionBar: {
                    actions: [],
                  },
                  day: {
                    closedDays,
                    getAppointmentCountsForDate: getAppointmentCountsForCalendarDate,
                    chairScheduledDates: chairScheduledDateKeys,
                    sx: {
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(46, 45, 45, .1)",
                        opacity: 1,
                        color: "white",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          </Box>{/* end blue box */}

          {/* Orange box: Dentist Schedule Summary for selected date */}
          <Box sx={{ backgroundColor: "#fefefe", borderRadius: "16px", border: "1px solid #0000010ff", boxShadow: "0 1px 5px rgba(0,0,0,0.4)", padding: "12px", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 700, fontSize: "13px" }}>
                Schedule Summary — {chairTableDateLabel}
              </Typography>
              {dentistScheduleSummaryForDate.some((e) => e.isReference) && (
                <Button
                  size="small"
                  onClick={() => setHideReferenceSummary((v) => !v)}
                  sx={{ fontSize: "10px", textTransform: "none", color: "#888", px: 1, py: 0.25, minWidth: 0, borderRadius: "6px", border: "1px solid rgba(0,0,0,0.12)", lineHeight: 1.4 }}
                >
                  {hideReferenceSummary ? "Show Reference" : "Hide Reference"}
                </Button>
              )}
            </Box>
            {dentistScheduleSummaryForDate.filter((e) => !hideReferenceSummary || !e.isReference).length === 0 ? (
              <Typography variant="body2" sx={{ color: "#999", fontSize: "12px" }}>
                No dentist schedules plotted for this date.
              </Typography>
            ) : (
              <Stack spacing={0.75} sx={{ overflowY: "auto", pr: 0.5 }}>
                {dentistScheduleSummaryForDate.filter((e) => !hideReferenceSummary || !e.isReference).map(({ uid, name, timeLabel, chairNumber, isReference, referenceKind }) => (
                  <Box
                    key={uid}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: isReference ? "rgba(0,0,0,0.03)" : "rgba(197,165,114,0.08)",
                      borderRadius: "8px",
                      border: isReference ? "1px dashed rgba(0,0,0,0.18)" : "1px solid rgba(197,165,114,0.3)",
                      padding: "6px 10px",
                      opacity: isReference ? 0.8 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: isReference ? "rgba(0,0,0,0.06)" : "rgba(197,165,114,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: "10px",
                        fontWeight: 700,
                        color: isReference ? "#888" : "#8a6a30",
                      }}
                    >
                      {chairNumber ? `C${chairNumber}` : "—"}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isReference ? "#555" : "#2C251E", lineHeight: 1.2 }}>
                        {name}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "#777" }}>
                        {timeLabel || "No time slots"}
                      </Typography>
                    </Box>
                    {isReference && (
                      <Box sx={{ flexShrink: 0, fontSize: "9px", fontWeight: 600, color: referenceKind === "date" ? "#7a5c00" : "#888", backgroundColor: referenceKind === "date" ? "rgba(197,165,114,0.18)" : "rgba(0,0,0,0.07)", borderRadius: "4px", px: 0.75, py: 0.25, whiteSpace: "nowrap" }}>
                        {referenceKind === "date" ? "Day Reference" : "Reference"}
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>{/* end left column wrapper */}

        {/* Right column: Chair Timetable */}
        <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
          {configuredChairCount > 0 ? (
            <Box sx={{ backgroundColor: "#fefefe", borderRadius: "16px", border: "1px solid #0000010ff", boxShadow: "0 1px 5px rgba(0,0,0,0.4)", padding: "16px" }}>
              <Typography variant="h6" sx={{ color: "#333", fontWeight: 600, fontSize: "16px", mb: 0.5 }}>
                Chair Schedule — {chairTableDateLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 1.5 }}>
                Real-time view of chair assignments and appointments for the selected date.
              </Typography>
              {!isChairScheduleReady ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "200px",
                    color: "#777",
                  }}
                >
                  <Typography variant="body2">Loading chair schedule for this date…</Typography>
                </Box>
              ) : (
              <Box sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 240px)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: `${180 + configuredChairCount * 160}px`, tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ backgroundColor: "rgba(197,165,114,0.15)", position: "sticky", top: 0, zIndex: 1 }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", color: "#555", fontWeight: 700, borderBottom: "2px solid rgba(197,165,114,0.4)", whiteSpace: "nowrap", minWidth: "110px", fontSize: "12px" }}>Time</th>
                      {chairTableData.slice(0, configuredChairCount).map(({ chairNum: cNum }) => (
                        <th key={cNum} style={{ padding: "8px 12px", textAlign: "left", color: "#555", fontWeight: 700, borderBottom: "2px solid rgba(197,165,114,0.4)", whiteSpace: "nowrap", minWidth: "160px", fontSize: "12px" }}>
                          <div>Chair {cNum}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timetableRows.map(({ slotLabel, chairCells, isChairFull, rowDurationMin }, rowIdx) => (
                      <tr
                        key={`${slotLabel}-${rowIdx}`}
                        style={{
                          borderBottom: "1px solid rgba(197,165,114,0.15)",
                          backgroundColor: isChairFull ? "rgba(80,80,80,0.06)" : rowIdx % 2 === 0 ? "white" : "rgba(197,165,114,0.03)",
                        }}
                      >
                        <td
                          style={{
                            padding: "7px 12px",
                            whiteSpace: "nowrap",
                            color: isChairFull ? "#999" : "#555",
                            fontSize: "12px",
                            fontWeight: 500,
                            verticalAlign: "top",
                            minHeight: `${Math.max(28, (rowDurationMin || 30) * 0.85)}px`,
                          }}
                        >
                          <span>{slotLabel}</span>
                          {isChairFull && (
                            <span style={{ display: "inline-block", marginLeft: "5px", fontSize: "9px", lineHeight: "14px", backgroundColor: "rgba(180,80,80,0.12)", color: "#b45309", border: "1px solid rgba(180,80,80,0.25)", borderRadius: "3px", padding: "0 4px", verticalAlign: "middle" }}>Full</span>
                          )}
                        </td>
                        {chairCells.slice(0, configuredChairCount).map(({ chairNum: cn, isAvailable, appointment, apptDentist, rowSpan, hidden }) => {
                          if (hidden) return null;
                          const hasAppt = !!appointment;
                          const apptStatus = appointment?.status?.toLowerCase();
                          let bg = isAvailable ? "rgba(232,245,233,0.6)" : "transparent";
                          let borderLeft = isAvailable ? "3px solid rgba(100,180,100,0.4)" : "3px solid transparent";
                          if (hasAppt) {
                            if (apptStatus === "confirmed") { bg = "rgba(43,103,67,0.12)"; borderLeft = "3px solid #2e7d32"; }
                            else if (apptStatus === "pending" || apptStatus === "new") { bg = "rgba(197,165,114,0.18)"; borderLeft = "3px solid #C5A572"; }
                            else if (apptStatus === "completed") { bg = "rgba(21,101,192,0.1)"; borderLeft = "3px solid #1565c0"; }
                          }
                          const patientName = appointment ? (appointment.patientName || appointment.name || appointment.firstName || "Patient") : null;
                          const dentistLabel = apptDentist?.displayName ?? null;
                          return (
                            <td key={cn} rowSpan={rowSpan > 1 ? rowSpan : undefined} style={{ padding: "5px 10px", fontSize: "12px", backgroundColor: bg, borderLeft, verticalAlign: "top" }}>
                              {!isAvailable && !hasAppt ? (
                                <span style={{ color: "#ccc", fontSize: "11px" }}>—</span>
                              ) : hasAppt ? (
                                <div>
                                  <div style={{ fontWeight: 600, color: apptStatus === "confirmed" ? "#2e7d32" : apptStatus === "pending" || apptStatus === "new" ? "#b45309" : apptStatus === "completed" ? "#1565c0" : "#555" }}>
                                    {patientName}
                                  </div>
                                  {dentistLabel && <div style={{ color: "#777", fontSize: "11px" }}>{dentistLabel}</div>}
                                  {(() => {
                                    const truth = getAppointmentDisplayRangeMinutes(appointment);
                                    if (!truth) return null;
                                    return (
                                      <div style={{ color: "#888", fontSize: "10px", marginTop: "2px" }}>
                                        {formatSlotRangeLabelFromMinutes(truth.start, truth.end)}
                                      </div>
                                    );
                                  })()}
                                  <div style={{ color: "#999", fontSize: "10px", marginTop: "1px", textTransform: "capitalize" }}>{apptStatus}</div>
                                </div>
                              ) : isAvailable ? (
                                <div style={{ color: "#4a7a50", fontSize: "11px" }}>Available</div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ backgroundColor: "white", borderRadius: "12px", border: "1px solid rgba(197,165,114,0.35)", padding: "32px", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
              <Typography variant="body2" sx={{ color: "#999", textAlign: "center" }}>
                Set the chair schedule for this date, then configure dentist schedules to view the timetable.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={showScheduleModal}
        onClose={() => (scheduleSaving ? null : setShowScheduleModal(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>Set Dentist Schedule</Box>
          <IconButton
            onClick={() => (scheduleSaving ? null : setShowScheduleModal(false))}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="schedule-dentist-label">Dentist</InputLabel>
              <Select
                labelId="schedule-dentist-label"
                label="Dentist"
                value={scheduleDentistUid}
                onChange={(e) => {
                  schedulePrefillKeyRef.current = "";
                  userEditedScheduleRef.current = false;
                  setScheduleDentistUid(e.target.value);
                }}
                MenuProps={MODAL_SELECT_MENU_PROPS}
              >
                {dentists.map((d) => {
                  const hasAssignedSchedule = dentistsWithScheduleOnSelectedDates.has(d.uid);
                  return (
                    <MenuItem
                      key={d.uid}
                      value={d.uid}
                      sx={
                        hasAssignedSchedule
                          ? {
                              backgroundColor: "rgba(197, 165, 114, 0.12) !important",
                              border: "2px solid rgba(197, 165, 114, 0.55) !important",
                              borderRadius: "8px",
                              margin: "2px 8px",
                              fontWeight: 600,
                              color: "#2C251E !important",
                              "&.Mui-selected": {
                                backgroundColor: "rgba(197, 165, 114, 0.25) !important",
                                color: "#2C251E !important",
                              },
                            }
                          : undefined
                      }
                    >
                      {d.displayName || getDentistDisplayName(d)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {(() => {
              const dayRef = scheduleDentistUid ? dentistReferenceScheduleForDate[scheduleDentistUid] : null;
              const dentist = scheduleDentistUid ? dentists.find((d) => d.uid === scheduleDentistUid) : null;
              const hasDefault = !!(dentist?.defaultAvailableFrom || dentist?.defaultAvailableTo);
              if (!dayRef && !hasDefault) return null;
              const label = dayRef ? "Fill from Day Reference" : "Fill from Reference";
              return (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const from = dayRef?.from || dentist?.defaultAvailableFrom || "09:00";
                    const to = dayRef?.to || dentist?.defaultAvailableTo || "18:00";
                    const [fh, fm] = from.split(":").map(Number);
                    const [th, tm] = to.split(":").map(Number);
                    const fromMin = fh * 60 + fm;
                    const toMin = th * 60 + tm;
                    const matchingSlots = STANDARD_OFFICE_SLOT_LABELS.filter((slot) => {
                      const parts = slot.split(" - ");
                      const s = parseClockLabelToMinutes(parts[0]);
                      const e = parseClockLabelToMinutes(parts[1]);
                      return s >= fromMin && e <= toMin;
                    });
                    userEditedScheduleRef.current = true;
                    setScheduleSlots(matchingSlots);
                  }}
                  sx={{
                    borderColor: "#C5A572",
                    color: "#C5A572",
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "12px",
                    alignSelf: "flex-start",
                    px: 2,
                  }}
                >
                  {label}
                </Button>
              );
            })()}

            <FormControl fullWidth size="small">
              <InputLabel id="schedule-slots-label">Available time slots</InputLabel>
              <Select
                labelId="schedule-slots-label"
                multiple
                value={scheduleSlots}
                onChange={() => {}}
                input={<OutlinedInput label="Available time slots" />}
                MenuProps={MODAL_SELECT_MENU_PROPS}
                renderValue={(selected) => {
                  if (!selected.length) return "Select time slots";
                  const selectedGroups = scheduleSlotMenuOptions.filter(
                    (option) =>
                      !option.segmentSlots.some((slot) => blockedScheduleSlots.has(slot)) &&
                      option.segmentSlots.every((slot) => selected.includes(slot))
                  ).length;
                  return `${selectedGroups} slot${selectedGroups === 1 ? "" : "s"} selected`;
                }}
              >
                {scheduleSlotMenuOptions.map((option) => {
                  const isBlocked = option.segmentSlots.some((slot) =>
                    blockedScheduleSlots.has(slot)
                  );
                  const isSelected =
                    !isBlocked &&
                    option.segmentSlots.every((slot) => scheduleSlots.includes(slot));
                  const isBooked = option.hasAppointment;
                  return (
                    <MenuItem
                      key={option.key}
                      value={option.segmentSlots[0]}
                      selected={isSelected && !isBlocked}
                      disabled={isBlocked}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleScheduleSlotOption(option);
                      }}
                      sx={
                        isBlocked
                          ? {
                              backgroundColor: "rgba(46, 45, 45, 0.12)",
                              cursor: "not-allowed",
                              "&.Mui-disabled": {
                                opacity: 1,
                              },
                              "& .MuiListItemText-primary": {
                                color: "#2C251E",
                              },
                              "& .MuiCheckbox-root.Mui-disabled": {
                                color: "rgba(197, 165, 114, 0.45)",
                              },
                            }
                          : isBooked && isSelected
                            ? {
                                backgroundColor: "rgba(197, 165, 114, 0.2)",
                                "& .MuiListItemText-primary": {
                                  color: "#2C251E",
                                  fontWeight: 600,
                                },
                              }
                            : undefined
                      }
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isBlocked}
                        sx={{
                          color: "#C5A572",
                          "&.Mui-disabled": {
                            color: isBlocked
                              ? "rgba(197, 165, 114, 0.45)"
                              : "rgba(0, 0, 0, 0.26)",
                          },
                        }}
                      />
                      <ListItemText primary={option.displayLabel} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ color: "#444", marginBottom: 1 }}>
                Select dates to apply this schedule ({scheduleDateCountLabel})
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  "& .react-calendar": {
                    width: "100%",
                    border: "1px solid rgba(197,165,114,0.35)",
                    borderRadius: "8px",
                  },
                  "& .react-calendar__tile": {
                    position: "relative",
                  },
                  "& .react-calendar__tile:disabled": {
                    backgroundColor: "rgba(46, 45, 45, 0.08) !important",
                    color: "#bbb !important",
                    cursor: "not-allowed",
                  },
                  "& .plot-chair-day": {
                    backgroundColor: "rgba(197, 165, 114, 0.12) !important",
                    color: "#2C251E !important",
                    fontWeight: "600 !important",
                    borderRadius: "8px",
                    border: "2px solid rgba(197, 165, 114, 0.55) !important",
                  },
                  "& .plot-assigned-day": {
                    backgroundColor: "rgba(197, 165, 114, 0.25) !important",
                    color: "#2C251E !important",
                    fontWeight: "600 !important",
                    borderRadius: "8px",
                    border: "2px solid #C5A572 !important",
                  },
                  "& .plot-selected-day": {
                    backgroundColor: "#C5A572 !important",
                    color: "white !important",
                    borderRadius: "8px",
                    border: "2px solid #C5A572 !important",
                  },
                }}
              >
                <Calendar
                  onClickDay={toggleSchedulePlotDate}
                  tileDisabled={({ date, view }) => {
                    if (view !== "month") return false;
                    const key = getLocalDateKeyFromDate(date);
                    return !chairScheduleByDateKey.has(key);
                  }}
                  tileClassName={({ date, view }) => {
                    if (view !== "month") return null;
                    const key = getLocalDateKeyFromDate(date);
                    if (scheduleDates.has(key)) return "plot-selected-day";
                    if (assignedPlotDates.has(key)) return "plot-assigned-day";
                    if (chairScheduleByDateKey.has(key)) return "plot-chair-day";
                    return null;
                  }}
                  tileContent={renderModalCalendarAppointmentBadges}
                />
              </Box>
            </Box>

            {scheduleError && (
              <Alert severity="error" icon={false}>
                {scheduleError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => (scheduleSaving ? null : setShowScheduleModal(false))}
            sx={{ color: "#555", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSchedule}
            disabled={scheduleSaving}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              textTransform: "none",
              "&:hover": { backgroundColor: "#b39463" },
            }}
          >
            {scheduleSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showChairScheduleModal}
        onClose={() => (chairScheduleSaving ? null : setShowChairScheduleModal(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", backgroundColor: "#f8f5ef" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#C5A572",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px 24px",
            position: "relative",
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>Set Chair Schedule</Box>
          <IconButton
            onClick={() => (chairScheduleSaving ? null : setShowChairScheduleModal(false))}
            sx={{
              position: "absolute",
              right: "24px",
              background: "transparent",
              border: "2px solid white",
              color: "white",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              "&:hover": { backgroundColor: "white", color: "#C5A572" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ color: "#444", mb: 1, fontWeight: 500 }}>
                Number of available physical dentist chairs
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <IconButton
                  onClick={() => {
                    setChairScheduleError(null);
                    setChairScheduleModalCount((prev) => Math.max(CHAIR_SCHEDULE_NONE, prev - 1));
                  }}
                  disabled={chairScheduleModalCount <= CHAIR_SCHEDULE_NONE}
                  sx={{
                    border: "1px solid rgba(197,165,114,0.6)",
                    borderRadius: "8px",
                    width: 36,
                    height: 36,
                    color: chairScheduleModalCount <= CHAIR_SCHEDULE_NONE ? "#bbb" : "#C5A572",
                    "&:hover": { backgroundColor: "rgba(197,165,114,0.12)" },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Box
                  sx={{
                    minWidth: 100,
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#2C251E",
                    border: "1px solid rgba(197,165,114,0.5)",
                    borderRadius: "8px",
                    py: 0.75,
                    px: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {chairScheduleModalCount === CHAIR_SCHEDULE_NONE
                    ? "None"
                    : `${chairScheduleModalCount} chair${chairScheduleModalCount === 1 ? "" : "s"}`}
                </Box>
                <IconButton
                  onClick={() => {
                    setChairScheduleError(null);
                    setChairScheduleModalCount((prev) => prev + 1);
                  }}
                  sx={{
                    border: "1px solid rgba(197,165,114,0.6)",
                    borderRadius: "8px",
                    width: 36,
                    height: 36,
                    color: "#C5A572",
                    "&:hover": { backgroundColor: "rgba(197,165,114,0.12)" },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              {chairScheduleModalCount === CHAIR_SCHEDULE_NONE && (
                <Typography variant="caption" sx={{ color: "#888", mt: 0.5, display: "block" }}>
                  Setting to None will clear the chair schedule for selected dates.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: "#444", marginBottom: 1 }}>
                Select dates to apply this chair count ({chairScheduleDateCountLabel})
              </Typography>
              <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
                Click dates to toggle selection. Use the − / + buttons above to set the number
                of physical chairs. Set to None (decrease to 0) to clear the chair schedule on
                selected dates. Dates with a gold outline already have a chair schedule configured.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  "& .react-calendar": {
                    width: "100%",
                    border: "1px solid rgba(197,165,114,0.35)",
                    borderRadius: "8px",
                  },
                  "& .react-calendar__tile": {
                    position: "relative",
                  },
                  "& .plot-assigned-day": {
                    backgroundColor: "rgba(197, 165, 114, 0.25) !important",
                    color: "#2C251E !important",
                    fontWeight: "600 !important",
                    borderRadius: "8px",
                    border: "2px solid #C5A572 !important",
                  },
                  "& .plot-selected-day": {
                    backgroundColor: "#C5A572 !important",
                    color: "white !important",
                    borderRadius: "8px",
                    border: "2px solid #C5A572 !important",
                  },
                }}
              >
                <Calendar
                  onClickDay={toggleChairScheduleDate}
                  tileClassName={({ date, view }) => {
                    if (view !== "month") return null;
                    const key = getLocalDateKeyFromDate(date);
                    if (chairScheduleDates.has(key)) return "plot-selected-day";
                    if (chairScheduleByDateKey.has(key)) return "plot-assigned-day";
                    return null;
                  }}
                  tileContent={renderModalCalendarAppointmentBadges}
                />
              </Box>
            </Box>

            {chairScheduleError && (
              <Alert severity="error" icon={false}>
                {chairScheduleError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => (chairScheduleSaving ? null : setShowChairScheduleModal(false))}
            sx={{ color: "#555", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveChairSchedule}
            disabled={isChairScheduleSaveDisabled}
            variant="contained"
            sx={{
              backgroundColor: "#C5A572",
              textTransform: "none",
              "&:hover": { backgroundColor: "#b39463" },
            }}
          >
            {chairScheduleSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={chairSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setChairSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={chairSnackbar.severity}
          onClose={() => setChairSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {chairSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ClinicStaffDashboardCalendar;
